import React, { useState, useEffect, useRef } from 'react';
import { Tag as TagIcon, Plus } from 'lucide-react';
import TagChip from './TagChip';
import { TAG_COLORS, suggestTagColor } from './tagPalette';
import './Tags.css';

/**
 * Multi-select tag picker with inline creation (WR-010).
 *
 * Built on the shape of the assignee dropdown next to it in TaskModal —
 * toggle button, search field, click-outside to close — so the sidebar has
 * one interaction pattern rather than two.
 *
 * Inline creation is the primary path: typing a name that matches nothing
 * offers "Create <name>" with a colour already suggested, and creating a tag
 * immediately attaches it. The manager exists for maintenance, not for
 * getting started.
 *
 * Props:
 *   tags        — the project's tag list (source of truth for name/colour)
 *   selectedIds — tag ids on this task
 *   onChange    — (nextIds) => void
 *   onCreateTag — async (name, color) => created tag; owned by the board so
 *                 a new tag lands in the project everywhere at once
 */
function TagPicker({ tags = [], selectedIds = [], onChange, onCreateTag }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [newColor, setNewColor] = useState(() => suggestTagColor(tags.map(t => t.color)));
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);

  const selected = new Set(selectedIds.map(String));

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  // A fresh suggestion every time the menu opens, drawn from the colours the
  // project isn't using yet — so consecutive tags don't all come out slate.
  useEffect(() => {
    if (open) {
      setNewColor(suggestTagColor(tags.map(t => t.color)));
      setError(null);
    }
  }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps

  const query = search.trim();
  const matches = tags.filter(t => t.name.toLowerCase().includes(query.toLowerCase()));
  const exactExists = tags.some(t => t.name.trim().toLowerCase() === query.toLowerCase());
  const canCreate = !!onCreateTag && query.length > 0 && query.length <= 30 && !exactExists;

  const toggle = (tagId) => {
    const id = String(tagId);
    const next = selected.has(id)
      ? selectedIds.filter(t => String(t) !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  const handleCreate = async () => {
    if (!canCreate || creating) return;
    setCreating(true);
    setError(null);
    try {
      const created = await onCreateTag(query, newColor);
      onChange([...selectedIds, String(created._id)]);
      setSearch('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create that tag.');
    } finally {
      setCreating(false);
    }
  };

  const selectedTags = tags.filter(t => selected.has(String(t._id)));

  return (
    <>
      {selectedTags.length > 0 && (
        <div className="tag-chip-row" style={{ marginBottom: '8px' }}>
          {selectedTags.map(tag => (
            <TagChip key={tag._id} tag={tag} onRemove={() => toggle(tag._id)} />
          ))}
        </div>
      )}

      <div className="tag-picker" ref={wrapRef}>
        <button type="button" className="tag-picker-toggle" onClick={() => setOpen(o => !o)}>
          <TagIcon size={14} />
          Add tag...
        </button>

        {open && (
          <div className="tag-picker-menu">
            <input
              type="text"
              className="tag-picker-search"
              placeholder="Search or create a tag..."
              value={search}
              onChange={e => { setSearch(e.target.value); setError(null); }}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); handleCreate(); }
                if (e.key === 'Escape') setOpen(false);
              }}
              autoFocus
            />

            <div className="tag-picker-list">
              {matches.map(tag => (
                <button
                  key={tag._id}
                  type="button"
                  className="tag-picker-item"
                  onClick={() => toggle(tag._id)}
                >
                  <TagChip tag={tag} />
                  {selected.has(String(tag._id)) && <span className="tag-picker-check">✓</span>}
                </button>
              ))}
              {matches.length === 0 && !canCreate && (
                <div className="tag-picker-empty">
                  {tags.length === 0 ? 'No tags in this project yet.' : 'No matching tags.'}
                </div>
              )}
            </div>

            {error && <div className="tag-picker-error">{error}</div>}

            {canCreate && (
              <div className="tag-picker-create">
                <div className="tag-picker-create-row">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleCreate}
                    disabled={creating}
                  >
                    <Plus size={14} />
                    {creating ? 'Creating...' : 'Create'}
                  </button>
                  <TagChip tag={{ name: query, color: newColor }} />
                </div>
                <div className="tag-swatches" style={{ padding: '0 12px 10px' }}>
                  {TAG_COLORS.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      title={c.label}
                      aria-label={c.label}
                      className={`tag-swatch tag-swatch-${c.id} ${newColor === c.id ? 'selected' : ''}`}
                      onClick={() => setNewColor(c.id)}
                    />
                  ))}
                </div>
                <div className="tag-picker-create-hint">
                  A colour is suggested; pick another if you like.
                </div>
              </div>
            )}

            {query.length > 30 && (
              <div className="tag-picker-error">Tag names are limited to 30 characters.</div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default TagPicker;
